// Thin typed HTTP layer for the live backend. All control-plane requests are
// cookie-session authenticated (Postgres-backed sessions + an opaque HttpOnly
// `gc_session` cookie), so we always send credentials and never store a token in
// JS. See specs/03-api.md "Admin / control-plane API".
//
// CSRF: the backend uses double-submit. register/login/me responses carry a
// `csrf_token`; we cache it here and echo it in the `X-CSRF-Token` header on every
// mutating (POST/PATCH/DELETE) request, or the backend returns 403.

import type { ApiErrorEnvelope } from "./types";
import { ApiError } from "./types";

// Host root of the backend. Paths below are absolute (`/admin/...` for the control
// plane, `/v1/projects/...` for the project-scoped data plane), so BASE is "" by
// default (same origin / dev proxy) or a full origin like "http://localhost:8080".
const BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

// --- CSRF token cache (double-submit) ---------------------------------------
let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}
export function getCsrfToken(): string | null {
  return csrfToken;
}

const MUTATING = new Set(["POST", "PATCH", "PUT", "DELETE"]);

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING.has(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: "include",
      headers: Object.keys(headers).length ? headers : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, "network_error", `Could not reach the API: ${(e as Error).message}`);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const env = data as ApiErrorEnvelope | undefined;
    throw new ApiError(
      res.status,
      env?.error?.code ?? "http_error",
      env?.error?.message ?? `Request failed with ${res.status}`,
    );
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const http = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, body?: unknown) => request<T>("POST", p, body),
  patch: <T>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  del: <T>(p: string) => request<T>("DELETE", p),
};
